import React, { useState, useEffect, useRef } from 'react';
import { X, Play, Pause, SkipBack, SkipForward, Volume2, VolumeX, Heart, Share2, Plus, Shuffle, Repeat } from 'lucide-react';

const ExpandedNowPlaying = ({ 
  song, 
  isPlaying, 
  onClose, 
  onPlayPause,
  onPrevious,
  onNext,
  currentTime,
  duration,
  onSeek,
  volume,
  onVolumeChange,
  isLiked,
  onToggleLike,
  onAddToPlaylist,
  onShare,
  isShuffleOn,
  isRepeatOn,
  onToggleShuffle,
  onToggleRepeat,
  playSong,
  user
}) => {
  const isAd = song?.isAd || false;
  const [recommendations, setRecommendations] = useState([]);
  const [loadingRecs, setLoadingRecs] = useState(false);
  const [playlists, setPlaylists] = useState([]);
  const [showPlaylistDropdown, setShowPlaylistDropdown] = useState(false);
  const canvasRef = useRef(null);
  const audioContextRef = useRef(null);
  const analyserRef = useRef(null);
  const dataArrayRef = useRef(null);
  const animationRef = useRef(null);

  // Fetch recommendations
  useEffect(() => {
    if (song?.id) {
      fetchRecommendations();
    }
  }, [song?.id]);

  // Fetch playlists when user is available
  useEffect(() => {
    if (user) {
      fetchPlaylists();
    }
  }, [user]);

  const fetchRecommendations = async () => {
    if (!song?.id) return;
    
    setLoadingRecs(true);
    try {
      const response = await fetch(
        `${process.env.REACT_APP_BACKEND_URL}/api/songs/${song.id}/recommendations`
      );
      
      if (response.ok) {
        const data = await response.json();
        setRecommendations(data);
      }
    } catch (error) {
      console.error('Error fetching recommendations:', error);
    } finally {
      setLoadingRecs(false);
    }
  };

  const fetchPlaylists = async () => {
    if (!user) return;
    
    try {
      const response = await fetch(
        `${process.env.REACT_APP_BACKEND_URL}/api/playlists/me`,
        {
          headers: { 'Authorization': `Bearer ${localStorage.getItem('auth_token')}` }
        }
      );
      if (response.ok) {
        const data = await response.json();
        setPlaylists(data);
      }
    } catch (error) {
      console.error('Failed to fetch playlists:', error);
    }
  };

  const handleAddToPlaylist = () => {
    if (!user) {
      alert('Please login to add songs to playlists');
      return;
    }
    setShowPlaylistDropdown(!showPlaylistDropdown);
  };

  const addToPlaylist = async (playlistId) => {
    try {
      const response = await fetch(
        `${process.env.REACT_APP_BACKEND_URL}/api/playlists/${playlistId}/songs/${song.id}`,
        {
          method: 'POST',
          headers: { 'Authorization': `Bearer ${localStorage.getItem('auth_token')}` }
        }
      );
      
      if (response.ok) {
        alert('Song added to playlist!');
        setShowPlaylistDropdown(false);
      } else {
        const errorData = await response.json().catch(() => ({}));
        alert(errorData.detail || 'Failed to add song to playlist');
      }
    } catch (error) {
      console.error('Failed to add song to playlist:', error);
      alert('Failed to add song to playlist');
    }
  };

  // Setup audio visualizer - simple animation that does NOT touch audio
  useEffect(() => {
    if (!song) return;

    const canvas = canvasRef.current;
    if (!canvas) return;

    const ctx = canvas.getContext('2d');
    const width = canvas.width;
    const height = canvas.height;
    let frameCount = 0;
    let lastFrameTime = 0;
    const fps = 30; // Limit to 30 FPS to reduce CPU/memory usage
    const frameInterval = 1000 / fps;

    // Pre-create gradient objects ONCE (not on every frame) - MEMORY OPTIMIZATION
    const gradients = [0, 1, 2].map(ripple => {
      const gradient = ctx.createLinearGradient(0, 0, width, height);
      gradient.addColorStop(0, `rgba(157, 78, 221, ${0.7 - ripple * 0.2})`);
      gradient.addColorStop(0.5, `rgba(199, 125, 255, ${0.7 - ripple * 0.2})`);
      gradient.addColorStop(1, `rgba(255, 215, 0, ${0.7 - ripple * 0.2})`);
      return gradient;
    });

    const draw = (currentTime) => {
      animationRef.current = requestAnimationFrame(draw);
      
      // Frame rate limiting
      const elapsed = currentTime - lastFrameTime;
      if (elapsed < frameInterval) return;
      
      lastFrameTime = currentTime - (elapsed % frameInterval);
      frameCount++;

      const centerX = width / 2;
      const centerY = height / 2;
      const radius = Math.min(width, height) / 2 - 20;

      // Clear canvas
      ctx.clearRect(0, 0, width, height);

      const time = Date.now() / 1000;

      // Draw animated ripple circles (reduced from 3 to 2 ripples)
      for (let ripple = 0; ripple < 2; ripple++) {
        ctx.beginPath();
        
        const numPoints = 60; // Further reduced from 80 for better performance
        for (let i = 0; i <= numPoints; i++) {
          const angle = (i / numPoints) * Math.PI * 2;
          
          // Create wave effect using sine
          const wave = Math.sin(angle * 6 + time * 1.5 + ripple) * 12;
          const rippleRadius = radius + wave - (ripple * 25);
          
          const x = centerX + Math.cos(angle) * rippleRadius;
          const y = centerY + Math.sin(angle) * rippleRadius;

          if (i === 0) {
            ctx.moveTo(x, y);
          } else {
            ctx.lineTo(x, y);
          }
        }

        ctx.closePath();
        
        // Use pre-created gradient (no memory allocation per frame)
        ctx.strokeStyle = gradients[ripple];
        ctx.lineWidth = 3 - ripple;
        ctx.stroke();
      }

      // Reduced logging frequency
      if (frameCount % 900 === 0) {
        console.log('🎨 Animation frame:', frameCount, 'FPS:', Math.round(1000 / elapsed));
      }
    };

    draw(0);

    return () => {
      if (animationRef.current) {
        cancelAnimationFrame(animationRef.current);
        animationRef.current = null;
      }
      // Clear canvas on unmount
      if (ctx) {
        ctx.clearRect(0, 0, width, height);
      }
      frameCount = 0;
      console.log('🧹 Animation cleaned up');
    };
  }, [song]);

  // Handle ESC key
  useEffect(() => {
    const handleEsc = (e) => {
      if (e.key === 'Escape') {
        if (showPlaylistDropdown) {
          setShowPlaylistDropdown(false);
        } else {
          onClose();
        }
      }
    };

    window.addEventListener('keydown', handleEsc);
    return () => window.removeEventListener('keydown', handleEsc);
  }, [onClose, showPlaylistDropdown]);

  // Close playlist dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (e) => {
      if (showPlaylistDropdown && !e.target.closest('button')) {
        setShowPlaylistDropdown(false);
      }
    };

    if (showPlaylistDropdown) {
      document.addEventListener('click', handleClickOutside);
      return () => document.removeEventListener('click', handleClickOutside);
    }
  }, [showPlaylistDropdown]);

  // Format time
  const formatTime = (seconds) => {
    if (!seconds || isNaN(seconds)) return '0:00';
    const mins = Math.floor(seconds / 60);
    const secs = Math.floor(seconds % 60);
    return `${mins}:${secs.toString().padStart(2, '0')}`;
  };

  if (!song) return null;

  return (
    <div 
      className="expanded-now-playing-overlay"
      onClick={onClose}
      style={{
        position: 'fixed',
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        background: 'rgba(0, 0, 0, 0.95)',
        zIndex: 9999,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        backdropFilter: 'blur(10px)'
      }}
    >
      <div 
        onClick={(e) => e.stopPropagation()}
        style={{
          maxWidth: window.innerWidth <= 768 ? '100%' : '1600px',
          width: window.innerWidth <= 768 ? '100%' : '95%',
          height: window.innerWidth <= 768 ? '100vh' : '90vh',
          background: 'linear-gradient(135deg, #1a1a1a 0%, #2d1b3d 100%)',
          borderRadius: window.innerWidth <= 768 ? '0' : '24px',
          padding: window.innerWidth <= 768 ? '20px' : '40px',
          position: 'relative',
          overflow: 'hidden',
          boxShadow: '0 20px 60px rgba(0, 0, 0, 0.8)',
          display: 'flex',
          flexDirection: 'column'
        }}
      >
        {/* Close Button */}
        <button
          onClick={onClose}
          style={{
            position: 'absolute',
            top: '20px',
            right: '20px',
            background: 'rgba(255, 255, 255, 0.1)',
            border: 'none',
            borderRadius: '50%',
            width: '48px',
            height: '48px',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            cursor: 'pointer',
            color: 'white',
            transition: 'all 0.2s',
            zIndex: 10
          }}
          onMouseOver={(e) => {
            e.currentTarget.style.background = 'rgba(255, 255, 255, 0.2)';
            e.currentTarget.style.transform = 'scale(1.1)';
          }}
          onMouseOut={(e) => {
            e.currentTarget.style.background = 'rgba(255, 255, 255, 0.1)';
            e.currentTarget.style.transform = 'scale(1)';
          }}
        >
          <X size={24} />
        </button>

        <div style={{ display: 'flex', flexDirection: 'column', gap: '20px', flex: 1, overflow: 'auto' }}>
          
          {/* Ad Website Link - Positioned above everything for ads */}
          {isAd && song.websiteUrl && (
            <div style={{ flexShrink: 0, marginBottom: '4px' }}>
              <a
                href={song.websiteUrl}
                target="_blank"
                rel="noopener noreferrer"
                style={{
                  display: 'inline-block',
                  padding: '12px 24px',
                  background: 'linear-gradient(135deg, #9D4EDD 0%, #C77DFF 100%)',
                  color: 'white',
                  textDecoration: 'none',
                  borderRadius: '24px',
                  fontSize: '14px',
                  fontWeight: '600',
                  boxShadow: '0 4px 16px rgba(157, 78, 221, 0.4)',
                  transition: 'all 0.2s'
                }}
                onMouseOver={(e) => {
                  e.currentTarget.style.transform = 'translateY(-2px)';
                  e.currentTarget.style.boxShadow = '0 6px 20px rgba(157, 78, 221, 0.6)';
                }}
                onMouseOut={(e) => {
                  e.currentTarget.style.transform = 'translateY(0)';
                  e.currentTarget.style.boxShadow = '0 4px 16px rgba(157, 78, 221, 0.4)';
                }}
              >
                Visit Website →
              </a>
            </div>
          )}
          
          {/* Top Section - Album Art + Player Controls Side by Side */}
          <div style={{ 
            display: 'flex', 
            gap: '40px', 
            flexShrink: 0,
            flexDirection: window.innerWidth <= 768 ? 'column' : 'row',
            alignItems: window.innerWidth <= 768 ? 'center' : 'flex-start'
          }}>
            {/* Left: Album Art + Visualizer + Song Info */}
            <div style={{ 
              flex: window.innerWidth <= 768 ? '1' : '0 0 380px', 
              display: 'flex', 
              flexDirection: 'column', 
              alignItems: 'center',
              width: window.innerWidth <= 768 ? '100%' : 'auto',
              maxWidth: window.innerWidth <= 768 ? '320px' : 'none'
            }}>
              {/* Album Art with Visualizer */}
              <div style={{ 
                position: 'relative', 
                width: window.innerWidth <= 768 ? '280px' : '380px', 
                height: window.innerWidth <= 768 ? '280px' : '380px', 
                marginBottom: '16px', 
                flexShrink: 0 
              }}>
              {/* Static background circle for visualizer */}
              <div style={{
                position: 'absolute',
                top: '50%',
                left: '50%',
                transform: 'translate(-50%, -50%)',
                width: window.innerWidth <= 768 ? '260px' : '360px',
                height: window.innerWidth <= 768 ? '260px' : '360px',
                borderRadius: '50%',
                border: '2px solid rgba(157, 78, 221, 0.3)',
                zIndex: 0
              }} />
              
              {/* Canvas for visualizer */}
              <canvas
                ref={canvasRef}
                width={window.innerWidth <= 768 ? 280 : 380}
                height={window.innerWidth <= 768 ? 280 : 380}
                style={{
                  position: 'absolute',
                  top: 0,
                  left: 0,
                  width: '100%',
                  height: '100%',
                  zIndex: 1
                }}
              />
              
              {/* Album Art */}
              <img
                src={song.albumArt || song.album_art_url}
                alt={song.title}
                style={{
                  position: 'absolute',
                  top: '50%',
                  left: '50%',
                  transform: 'translate(-50%, -50%)',
                  width: window.innerWidth <= 768 ? '220px' : '300px',
                  height: window.innerWidth <= 768 ? '220px' : '300px',
                  borderRadius: '50%',
                  objectFit: 'cover',
                  boxShadow: '0 8px 32px rgba(0, 0, 0, 0.6)',
                  zIndex: 2
                }}
              />
            </div>

              {/* Song Info - Only show for regular songs, not ads */}
              {!isAd && (
                <div style={{ textAlign: 'center', width: '100%' }}>
                  <h1 style={{ 
                    fontSize: window.innerWidth <= 768 ? '22px' : '28px', 
                    marginBottom: '12px', 
                    color: 'white',
                    fontWeight: '700',
                    lineHeight: '1.2'
                  }}>
                    {song.title}
                  </h1>
                  
                  {/* Metadata */}
                  <div style={{ display: 'flex', flexWrap: 'wrap', gap: '8px', justifyContent: 'center', marginTop: '12px' }}>
                    {song.album && (
                      <span style={{
                        padding: '4px 12px',
                        background: 'rgba(157, 78, 221, 0.2)',
                        borderRadius: '16px',
                        fontSize: '12px',
                        color: '#C77DFF'
                      }}>
                        {song.album}
                      </span>
                    )}
                    {song.genre && (
                      <span style={{
                        padding: '4px 12px',
                        background: 'rgba(255, 215, 0, 0.2)',
                        borderRadius: '16px',
                        fontSize: '12px',
                        color: '#FFD700'
                      }}>
                        {song.genre}
                      </span>
                    )}
                    {/* Vibes */}
                    {song.vibes && song.vibes.length > 0 && song.vibes.slice(0, 2).map((vibe, idx) => (
                      <span 
                        key={idx}
                        style={{
                          padding: '4px 12px',
                          background: 'rgba(199, 125, 255, 0.15)',
                          borderRadius: '16px',
                          fontSize: '12px',
                          color: '#C77DFF',
                          textTransform: 'capitalize'
                        }}
                      >
                        {vibe.replace('-', ' ')}
                      </span>
                    ))}
                  </div>
                </div>
              )}

              {/* Ad Info - Show smaller indicator for ads */}
              {isAd && (
                <div style={{ textAlign: 'center', width: '100%' }}>
                  <div style={{ 
                    color: '#FFD700', 
                    fontSize: '10px', 
                    fontWeight: '600', 
                    textTransform: 'uppercase',
                    letterSpacing: '1px',
                    opacity: 0.7
                  }}>
                    Advertisement
                  </div>
                </div>
              )}
            </div>

            {/* Right: Player Controls */}
            <div style={{ 
              flex: 1, 
              display: 'flex', 
              flexDirection: 'column', 
              justifyContent: 'center', 
              gap: '24px', 
              paddingTop: window.innerWidth <= 768 ? '20px' : '40px',
              width: window.innerWidth <= 768 ? '100%' : 'auto'
            }}>
              
              {/* Progress Bar */}
            <div style={{ width: '100%', marginBottom: '20px' }}>
              <input
                type="range"
                min="0"
                max={duration || 100}
                value={currentTime || 0}
                onChange={(e) => onSeek(parseFloat(e.target.value))}
                style={{
                  width: '100%',
                  height: '6px',
                  borderRadius: '3px',
                  background: 'rgba(255, 255, 255, 0.2)',
                  outline: 'none',
                  cursor: 'pointer'
                }}
              />
              <div style={{ display: 'flex', justifyContent: 'space-between', marginTop: '8px', fontSize: '14px', color: '#999' }}>
                <span>{formatTime(currentTime)}</span>
                <span>{formatTime(duration)}</span>
              </div>
            </div>

            {/* Playback Controls */}
            <div style={{ display: 'flex', alignItems: 'center', gap: '24px', marginBottom: '20px' }}>
              <button
                onClick={onToggleShuffle}
                style={{
                  background: 'none',
                  border: 'none',
                  color: isShuffleOn ? '#9D4EDD' : '#666',
                  cursor: 'pointer',
                  padding: '8px',
                  transition: 'color 0.2s'
                }}
              >
                <Shuffle size={20} />
              </button>
              
              <button
                onClick={onPrevious}
                style={{
                  background: 'rgba(255, 255, 255, 0.1)',
                  border: 'none',
                  borderRadius: '50%',
                  width: '48px',
                  height: '48px',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  cursor: 'pointer',
                  color: 'white',
                  transition: 'all 0.2s'
                }}
                onMouseOver={(e) => e.currentTarget.style.background = 'rgba(255, 255, 255, 0.2)'}
                onMouseOut={(e) => e.currentTarget.style.background = 'rgba(255, 255, 255, 0.1)'}
              >
                <SkipBack size={24} />
              </button>

              <button
                onClick={onPlayPause}
                style={{
                  background: 'linear-gradient(135deg, #9D4EDD 0%, #C77DFF 100%)',
                  border: 'none',
                  borderRadius: '50%',
                  width: '72px',
                  height: '72px',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  cursor: 'pointer',
                  color: 'white',
                  boxShadow: '0 4px 16px rgba(157, 78, 221, 0.4)',
                  transition: 'all 0.2s'
                }}
                onMouseOver={(e) => e.currentTarget.style.transform = 'scale(1.05)'}
                onMouseOut={(e) => e.currentTarget.style.transform = 'scale(1)'}
              >
                {isPlaying ? <Pause size={32} /> : <Play size={32} style={{ marginLeft: '4px' }} />}
              </button>

              <button
                onClick={onNext}
                style={{
                  background: 'rgba(255, 255, 255, 0.1)',
                  border: 'none',
                  borderRadius: '50%',
                  width: '48px',
                  height: '48px',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  cursor: 'pointer',
                  color: 'white',
                  transition: 'all 0.2s'
                }}
                onMouseOver={(e) => e.currentTarget.style.background = 'rgba(255, 255, 255, 0.2)'}
                onMouseOut={(e) => e.currentTarget.style.background = 'rgba(255, 255, 255, 0.1)'}
              >
                <SkipForward size={24} />
              </button>

              <button
                onClick={onToggleRepeat}
                style={{
                  background: 'none',
                  border: 'none',
                  color: isRepeatOn ? '#9D4EDD' : '#666',
                  cursor: 'pointer',
                  padding: '8px',
                  transition: 'color 0.2s'
                }}
              >
                <Repeat size={20} />
              </button>
            </div>

            {/* Action Buttons */}
            <div style={{ display: 'flex', gap: '12px', justifyContent: 'center' }}>
              <button
                onClick={() => onToggleLike(song.id)}
                style={{
                  padding: '12px 20px',
                  background: isLiked ? 'rgba(239, 68, 68, 0.2)' : 'rgba(255, 255, 255, 0.1)',
                  border: `1px solid ${isLiked ? '#ef4444' : 'rgba(255, 255, 255, 0.2)'}`,
                  borderRadius: '24px',
                  color: isLiked ? '#ef4444' : 'white',
                  cursor: 'pointer',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '8px',
                  transition: 'all 0.2s'
                }}
              >
                <Heart size={18} fill={isLiked ? '#ef4444' : 'none'} />
                {isLiked ? 'Liked' : 'Like'}
              </button>

              <div style={{ position: 'relative' }}>
                <button
                  onClick={handleAddToPlaylist}
                  style={{
                    padding: '12px 20px',
                    background: 'rgba(255, 255, 255, 0.1)',
                    border: '1px solid rgba(255, 255, 255, 0.2)',
                    borderRadius: '24px',
                    color: 'white',
                    cursor: 'pointer',
                    display: 'flex',
                    alignItems: 'center',
                    gap: '8px',
                    transition: 'all 0.2s'
                  }}
                >
                  <Plus size={18} />
                  Add to Playlist
                </button>
                
                {showPlaylistDropdown && user && (
                  <div style={{
                    position: 'absolute',
                    top: '100%',
                    left: '0',
                    marginTop: '8px',
                    background: '#1a1a1a',
                    border: '1px solid rgba(255, 255, 255, 0.2)',
                    borderRadius: '8px',
                    minWidth: '200px',
                    maxHeight: '300px',
                    overflowY: 'auto',
                    zIndex: 1000,
                    boxShadow: '0 4px 20px rgba(0, 0, 0, 0.5)'
                  }}>
                    {playlists.length === 0 ? (
                      <div style={{
                        padding: '16px',
                        color: '#999',
                        fontSize: '14px',
                        textAlign: 'center'
                      }}>
                        No playlists yet. Create one from the sidebar!
                      </div>
                    ) : (
                      playlists.map(playlist => (
                        <button
                          key={playlist.id}
                          onClick={() => addToPlaylist(playlist.id)}
                          style={{
                            width: '100%',
                            padding: '12px 16px',
                            background: 'transparent',
                            border: 'none',
                            color: 'white',
                            textAlign: 'left',
                            cursor: 'pointer',
                            fontSize: '14px',
                            transition: 'background 0.2s',
                            display: 'flex',
                            alignItems: 'center',
                            gap: '8px'
                          }}
                          onMouseEnter={(e) => {
                            e.currentTarget.style.background = 'rgba(255, 255, 255, 0.1)';
                          }}
                          onMouseLeave={(e) => {
                            e.currentTarget.style.background = 'transparent';
                          }}
                        >
                          🎵 {playlist.name} ({playlist.song_ids?.length || 0})
                        </button>
                      ))
                    )}
                  </div>
                )}
              </div>

              <button
                onClick={() => onShare(song)}
                style={{
                  padding: '12px 20px',
                  background: 'rgba(255, 255, 255, 0.1)',
                  border: '1px solid rgba(255, 255, 255, 0.2)',
                  borderRadius: '24px',
                  color: 'white',
                  cursor: 'pointer',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '8px',
                  transition: 'all 0.2s'
                }}
              >
                <Share2 size={18} />
                Share
              </button>
            </div>

              {/* Volume Control */}
              <div style={{ width: '100%' }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                  <button
                    onClick={() => onVolumeChange(volume === 0 ? 0.5 : 0)}
                    style={{
                      background: 'none',
                      border: 'none',
                      color: 'white',
                      cursor: 'pointer',
                      padding: '8px'
                    }}
                  >
                    {volume === 0 ? <VolumeX size={20} /> : <Volume2 size={20} />}
                  </button>
                  <input
                    type="range"
                    min="0"
                    max="1"
                    step="0.01"
                    value={volume}
                    onChange={(e) => onVolumeChange(parseFloat(e.target.value))}
                    style={{
                      flex: 1,
                      height: '4px',
                      borderRadius: '2px',
                      background: 'rgba(255, 255, 255, 0.2)',
                      outline: 'none',
                      cursor: 'pointer'
                    }}
                  />
                </div>
              </div>
            </div>
          </div>

          {/* Bottom Section - Recommendations (only for non-ads and when recommendations exist) */}
          {!isAd && (loadingRecs || recommendations.length > 0) && (
            /* Regular Song View - Show Recommendations */
            <div style={{ flexShrink: 0, paddingBottom: '20px' }}>
              <h2 style={{ fontSize: '20px', marginBottom: '16px', color: 'white' }}>
                You Might Also Like
              </h2>

              {loadingRecs ? (
                <div style={{ textAlign: 'center', padding: '40px', color: '#999' }}>
                  Loading recommendations...
                </div>
              ) : (
                <div style={{
                  display: 'grid',
                  gridTemplateColumns: window.innerWidth <= 768 ? '1fr' : 'repeat(4, 1fr)',
                  gap: '12px'
                }}>
                  {recommendations.map((recSong) => (
                  <div
                    key={recSong.id}
                    onClick={() => playSong(recSong)}
                    style={{
                      background: 'rgba(255, 255, 255, 0.05)',
                      borderRadius: '12px',
                      padding: '12px',
                      display: 'flex',
                      gap: '12px',
                      cursor: 'pointer',
                      transition: 'all 0.2s',
                      border: '1px solid transparent'
                    }}
                    onMouseOver={(e) => {
                      e.currentTarget.style.background = 'rgba(255, 255, 255, 0.1)';
                      e.currentTarget.style.borderColor = 'rgba(157, 78, 221, 0.4)';
                    }}
                    onMouseOut={(e) => {
                      e.currentTarget.style.background = 'rgba(255, 255, 255, 0.05)';
                      e.currentTarget.style.borderColor = 'transparent';
                    }}
                  >
                    <img
                      src={recSong.albumArt || recSong.album_art_url}
                      alt={recSong.title}
                      style={{
                        width: '80px',
                        height: '80px',
                        borderRadius: '8px',
                        objectFit: 'cover'
                      }}
                    />
                    <div style={{ flex: 1, minWidth: 0 }}>
                      <h4 style={{
                        fontSize: '16px',
                        fontWeight: '600',
                        marginBottom: '4px',
                        color: 'white',
                        whiteSpace: 'nowrap',
                        overflow: 'hidden',
                        textOverflow: 'ellipsis'
                      }}>
                        {recSong.title}
                      </h4>
                      <p style={{
                        fontSize: '14px',
                        color: '#999',
                        marginBottom: '8px',
                        whiteSpace: 'nowrap',
                        overflow: 'hidden',
                        textOverflow: 'ellipsis'
                      }}>
                        {recSong.artist}
                      </p>
                      <div style={{ display: 'flex', gap: '6px', flexWrap: 'wrap' }}>
                        {recSong.genre && (
                          <span style={{
                            padding: '2px 8px',
                            background: 'rgba(255, 215, 0, 0.15)',
                            borderRadius: '8px',
                            fontSize: '11px',
                            color: '#FFD700'
                          }}>
                            {recSong.genre}
                          </span>
                        )}
                      </div>
                    </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default ExpandedNowPlaying;
